import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle, ArrowLeft, User, Users, UserPlus,
  LayoutDashboard, Send, Copy, Brain, Star, Sparkles, Calendar,
  AlertCircle
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';

type WizardStep = 1 | 2 | 3 | 4;
type OnboardingChoice = 'take_myself' | 'invite_client' | 'invite_prospect' | 'explore_dashboard';

export function FreeProfileOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState<WizardStep>(1);
  const [choice, setChoice] = useState<OnboardingChoice | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [assessmentCode, setAssessmentCode] = useState<string>('');
  const [invitationSent, setInvitationSent] = useState(false);
  const [invitationLink, setInvitationLink] = useState('');
  const [selfProfileStarted, setSelfProfileStarted] = useState(false);
  const [bookingUrl, setBookingUrl] = useState('');
  const [inviteForm, setInviteForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('coaches')
      .select('id, assessment_code, free_profile_onboarding_completed, account_plan, partner_id')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) {
          navigate('/coach');
          return;
        }
        if (data.account_plan !== 'FREE_PROFILE_COACH') {
          navigate('/coach');
          return;
        }
        setCoachId(data.id);
        setAssessmentCode(data.assessment_code);
        if (data.free_profile_onboarding_completed) {
          navigate('/coach');
        }
        if (data.partner_id) {
          supabase
            .from('partners')
            .select('booking_url')
            .eq('id', data.partner_id)
            .maybeSingle()
            .then(({ data: partner }) => {
              if (partner?.booking_url) setBookingUrl(partner.booking_url);
            });
        }
      });
  }, [user?.id, navigate]);

  const logFunnelEvent = async (eventType: string, metadata?: any) => {
    if (!coachId) return;
    try {
      await supabase.from('free_profile_funnel_events').insert({
        coach_id: coachId,
        event_type: eventType,
        event_metadata: metadata || {},
      });
    } catch {
      // best-effort
    }
  };

  const completeOnboarding = async () => {
    if (!coachId) return;
    await supabase
      .from('coaches')
      .update({
        free_profile_onboarding_completed: true,
        free_profile_onboarding_choice: choice,
        updated_at: new Date().toISOString(),
      })
      .eq('id', coachId);
    await logFunnelEvent('wizard_completed', { choice });
    navigate('/coach');
  };

  const handleStep2Continue = () => {
    if (!choice) return;
    logFunnelEvent('wizard_step2_choice', { choice });
    setStep(3);
  };

  const handleStartMyProfile = async () => {
    if (!user?.id || !coachId) return;
    setIsLoading(true);
    setError(null);
    try {
      const { data: existing } = await supabase
        .from('respondents')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!existing) {
        const { error: respError } = await supabase.from('respondents').insert({
          user_id: user.id,
          coach_id: coachId,
          ai_analysis_enabled: true,
          ai_analysis_visible: true,
        });
        if (respError) throw respError;
      }

      await logFunnelEvent('self_assessment_started');
      setSelfProfileStarted(true);
      setStep(4);
    } catch (err: any) {
      setError(err?.message || 'Failed to start your Profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!coachId || !inviteForm.firstName || !inviteForm.email) return;
    setIsLoading(true);
    setError(null);
    try {
      const link = `https://app.humanpotentialos.com/register?code=${assessmentCode}`;
      setInvitationLink(link);

      await supabase
        .from('coaches')
        .update({
          free_profile_first_invitation_date: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', coachId);

      await logFunnelEvent('first_invitation_sent', {
        respondent_email: inviteForm.email,
        respondent_name: `${inviteForm.firstName} ${inviteForm.lastName}`,
      });

      // Record in pending invitations so it shows in the dashboard immediately
      await supabase.from('free_profile_pending_invitations').upsert({
        coach_id: coachId,
        email: inviteForm.email,
        first_name: inviteForm.firstName,
        last_name: inviteForm.lastName || null,
        invited_at: new Date().toISOString(),
      }, { onConflict: 'coach_id,email' });

      // Send the invitation email to the respondent
      try {
        const coachName = user?.user_metadata?.full_name || user?.email || '';
        await supabase.functions.invoke('send-email', {
          body: {
            type: 'free_profile_respondent_invitation',
            recipientEmail: inviteForm.email,
            recipientName: `${inviteForm.firstName} ${inviteForm.lastName}`.trim(),
            coachName,
            registrationLink: link,
          },
        });
      } catch {
        // best-effort
      }

      // Notify the coach that their first invitation was sent
      try {
        await supabase.functions.invoke('send-email', {
          body: {
            type: 'free_profile_invitation_sent',
            recipientEmail: user?.email || '',
            recipientName: user?.user_metadata?.full_name || '',
            respondentName: `${inviteForm.firstName} ${inviteForm.lastName}`.trim(),
          },
        });
      } catch {
        // best-effort
      }

      setInvitationSent(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to send invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const copyInvitationLink = () => {
    if (invitationLink) {
      navigator.clipboard.writeText(invitationLink);
    }
  };

  const stepChoices = [
    {
      id: 'take_myself' as OnboardingChoice,
      icon: User,
      title: 'Take the Profile Myself',
      description: 'Experience the assessment as a respondent and see the type of insights your clients will receive.',
    },
    {
      id: 'invite_client' as OnboardingChoice,
      icon: Users,
      title: 'Invite an Existing Client',
      description: 'Use the Profile to deepen your understanding of someone you already coach.',
    },
    {
      id: 'invite_prospect' as OnboardingChoice,
      icon: UserPlus,
      title: 'Invite a Prospect',
      description: 'Use the Profile to create a valuable first conversation and identify potential coaching needs.',
    },
    {
      id: 'explore_dashboard' as OnboardingChoice,
      icon: LayoutDashboard,
      title: 'Explore My Dashboard First',
      description: 'Review the account before deciding who to invite.',
    },
  ];

  if (!coachId) {
    return <LoadingSpinner message="Loading your onboarding..." />;
  }

  return (
    <div className="min-h-screen freud-bg py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`h-2 rounded-full transition-all duration-300 ${
                s === step ? 'w-12 bg-sage-600' : s < step ? 'w-8 bg-sage-400' : 'w-8 bg-chocolate-200'
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Welcome */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="freud-card-elevated p-8"
            >
              <div className="text-center mb-8">
                <div className="inline-flex p-4 bg-sage-100 rounded-2xl mb-6">
                  <Sparkles className="w-8 h-8 text-sage-600" />
                </div>
                <h1 className="freud-heading-xl text-chocolate-900 mb-4">
                  Welcome to Your Human Potential Profile Coach Account
                </h1>
                <p className="freud-text-base text-chocolate-500 max-w-xl mx-auto">
                  You now have three complimentary Profile experiences to use with yourself, a client, a prospect or a colleague.
                </p>
                <p className="freud-text-sm text-chocolate-400 max-w-xl mx-auto mt-3">
                  Use them to experience how the Profile can help you understand people more deeply, create more valuable conversations and identify meaningful coaching opportunities.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3 bg-cream-100 rounded-xl p-4">
                  <Star className="w-5 h-5 text-sage-600 flex-shrink-0" />
                  <span className="freud-text-sm text-chocolate-700 font-medium">3 complimentary completed assessments</span>
                </div>
                <div className="flex items-center gap-3 bg-cream-100 rounded-xl p-4">
                  <Brain className="w-5 h-5 text-sage-600 flex-shrink-0" />
                  <span className="freud-text-sm text-chocolate-700 font-medium">AI-assisted analysis included</span>
                </div>
                <div className="flex items-center gap-3 bg-cream-100 rounded-xl p-4">
                  <CheckCircle className="w-5 h-5 text-sage-600 flex-shrink-0" />
                  <span className="freud-text-sm text-chocolate-700 font-medium">No credit card required</span>
                </div>
                <div className="flex items-center gap-3 bg-cream-100 rounded-xl p-4">
                  <Sparkles className="w-5 h-5 text-sage-600 flex-shrink-0" />
                  <span className="freud-text-sm text-chocolate-700 font-medium">Profile access only</span>
                </div>
              </div>

              <button onClick={() => { logFunnelEvent('wizard_started'); setStep(2); }} className="freud-btn-primary w-full">
                Continue
              </button>
            </motion.div>
          )}

          {/* Step 2: Choose how to begin */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="freud-card-elevated p-8"
            >
              <h1 className="freud-heading-lg text-chocolate-900 mb-2 text-center">
                How Would You Like to Experience the Profile First?
              </h1>
              <p className="freud-text-sm text-chocolate-500 mb-8 text-center">
                Select any option to continue through the onboarding.
              </p>

              <div className="space-y-3 mb-8">
                {stepChoices.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setChoice(opt.id)}
                    className={`w-full flex items-start gap-4 p-5 rounded-xl border-2 transition-all text-left ${
                      choice === opt.id
                        ? 'border-sage-600 bg-sage-50'
                        : 'border-chocolate-200 hover:border-sage-300 bg-white'
                    }`}
                  >
                    <div className={`p-2.5 rounded-xl flex-shrink-0 ${choice === opt.id ? 'bg-sage-600' : 'bg-cream-100'}`}>
                      <opt.icon className={`w-5 h-5 ${choice === opt.id ? 'text-white' : 'text-chocolate-500'}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="freud-text-base font-semibold text-chocolate-900 mb-1">{opt.title}</h3>
                      <p className="freud-text-sm text-chocolate-500">{opt.description}</p>
                    </div>
                    {choice === opt.id && <CheckCircle className="w-5 h-5 text-sage-600 flex-shrink-0 mt-1" />}
                  </button>
                ))}
              </div>

              <button onClick={handleStep2Continue} disabled={!choice} className="freud-btn-primary w-full">
                Continue
              </button>
            </motion.div>
          )}

          {/* Step 3: Create first Profile experience */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="freud-card-elevated p-8"
            >
              {/* Take the Profile Myself */}
              {choice === 'take_myself' && (
                <>
                  <h1 className="freud-heading-lg text-chocolate-900 mb-4 text-center">Take the Profile Myself</h1>
                  <p className="freud-text-sm text-chocolate-500 mb-8 text-center max-w-lg mx-auto">
                    Experience the assessment as a respondent and see the type of insights your clients will receive. We will create a respondent record connected to your coach account.
                  </p>
                  {error && (
                    <div className="flex items-center gap-2 bg-warm-100 text-warm-700 p-4 rounded-xl mb-6">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <p className="freud-text-sm">{error}</p>
                    </div>
                  )}
                  <button onClick={handleStartMyProfile} disabled={isLoading} className="freud-btn-primary w-full">
                    {isLoading ? 'Starting...' : 'Start My Profile'}
                  </button>
                </>
              )}

              {/* Invite Client / Prospect */}
              {(choice === 'invite_client' || choice === 'invite_prospect') && (
                <>
                  <h1 className="freud-heading-lg text-chocolate-900 mb-2 text-center">
                    {choice === 'invite_client' ? 'Invite an Existing Client' : 'Invite a Prospect'}
                  </h1>
                  <p className="freud-text-sm text-chocolate-500 mb-4 text-center">
                    {choice === 'invite_client'
                      ? 'Use the Profile to deepen your understanding of someone you already coach.'
                      : 'Use the Profile to create a valuable first conversation and identify potential coaching needs.'}
                  </p>

                  {invitationSent ? (
                    <div className="text-center mb-6">
                      <div className="inline-flex p-4 bg-sage-100 rounded-2xl mb-4">
                        <CheckCircle className="w-8 h-8 text-sage-600" />
                      </div>
                      <h3 className="freud-heading-base text-chocolate-900 mb-2">Invitation Sent</h3>
                      <p className="freud-text-sm text-chocolate-500 mb-1">
                        We have sent the invitation to {inviteForm.firstName} {inviteForm.lastName}.
                      </p>
                      <p className="freud-text-xs text-chocolate-400 mb-4">
                        You can send more invitations and track who has registered from your dashboard at any time.
                      </p>
                      <div className="bg-cream-100 rounded-xl p-4 mb-4 text-left">
                        <p className="freud-text-xs text-chocolate-400 mb-1">Assessment Link:</p>
                        <p className="freud-text-sm text-chocolate-700 font-mono break-all">{invitationLink}</p>
                      </div>
                      <button onClick={copyInvitationLink} className="freud-btn-primary w-full mb-3">
                        <Copy className="w-4 h-4 inline mr-2" />
                        Copy Invitation Link
                      </button>
                      <button onClick={() => setStep(4)} className="text-sage-600 hover:text-sage-700 font-medium freud-text-sm">
                        Continue to next step
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="bg-sage-50 border border-sage-200 rounded-xl p-4 mb-6 text-left">
                        <p className="freud-text-sm text-sage-700">
                          Enter the details of one person to invite now — we will send them an email with your registration link. You can invite more people from your dashboard once you complete this setup.
                        </p>
                        <p className="freud-text-sm text-sage-600 mt-2">
                          On the next screen you will also receive your full shareable link to send via WhatsApp, SMS or social media.
                        </p>
                      </div>

                      {error && (
                        <div className="flex items-center gap-2 bg-warm-100 text-warm-700 p-4 rounded-xl mb-6">
                          <AlertCircle className="w-5 h-5 flex-shrink-0" />
                          <p className="freud-text-sm">{error}</p>
                        </div>
                      )}
                      <div className="space-y-4 mb-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="freud-label block text-chocolate-800 mb-2">Respondent First Name</label>
                            <input
                              type="text"
                              value={inviteForm.firstName}
                              onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                              className="freud-input"
                              placeholder="John"
                            />
                          </div>
                          <div>
                            <label className="freud-label block text-chocolate-800 mb-2">Respondent Last Name</label>
                            <input
                              type="text"
                              value={inviteForm.lastName}
                              onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                              className="freud-input"
                              placeholder="Doe"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="freud-label block text-chocolate-800 mb-2">Respondent Email</label>
                          <input
                            type="email"
                            value={inviteForm.email}
                            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                            className="freud-input"
                            placeholder="person@example.com"
                          />
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <button onClick={handleSendInvitation} disabled={isLoading || !inviteForm.firstName || !inviteForm.email} className="freud-btn-primary flex-1">
                          {isLoading ? 'Sending...' : 'Send Invitation'}
                        </button>
                        <button onClick={() => setStep(4)} className="px-6 py-3 rounded-xl border-2 border-chocolate-200 text-chocolate-600 font-medium hover:bg-cream-100 transition-all">
                          Skip for Now
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Explore Dashboard */}
              {choice === 'explore_dashboard' && (
                <>
                  <h1 className="freud-heading-lg text-chocolate-900 mb-4 text-center">Explore Your Dashboard</h1>
                  <p className="freud-text-sm text-chocolate-500 mb-8 text-center max-w-lg mx-auto">
                    You can invite someone or take the Profile yourself at any time from your dashboard.
                  </p>
                  <button onClick={() => setStep(4)} className="freud-btn-primary w-full">
                    Continue
                  </button>
                </>
              )}
            </motion.div>
          )}

          {/* Step 4: Founder success session */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="freud-card-elevated p-8"
            >
              <div className="text-center mb-6">
                <div className="inline-flex p-4 bg-coral-100 rounded-2xl mb-4">
                  <Calendar className="w-8 h-8 text-coral-600" />
                </div>
                <h1 className="freud-heading-lg text-chocolate-900 mb-3">
                  Turn Your Profile Into a Powerful Client Conversation
                </h1>
                <p className="freud-text-sm text-chocolate-500 max-w-xl mx-auto">
                  As part of your new Human Potential Profile coach account, you are invited to attend a private Profile-to-Paid-Client Success Session with Patrick Dahdal, Founder of Human Potential OS.
                </p>
              </div>

              <div className="bg-cream-100 rounded-xl p-6 mb-6">
                <p className="freud-text-sm text-chocolate-600 mb-4 font-medium">During this practical one-to-one session, Patrick will help you:</p>
                <ul className="space-y-2">
                  {[
                    'identify the best people to invite',
                    'position the Profile so prospects want to complete it',
                    'prepare using the AI-assisted analysis',
                    'conduct a valuable and personal Profile review',
                    'identify the person\u2019s greatest coaching opportunity',
                    'transition naturally into a paid coaching conversation',
                    'create a repeatable Profile-to-client process',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-sage-600 flex-shrink-0 mt-0.5" />
                      <span className="freud-text-sm text-chocolate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="text-center mb-6">
                <p className="freud-text-sm text-chocolate-500">
                  You will leave with a clear invitation approach, Profile review structure and next-step conversation you can begin using immediately.
                </p>
                <p className="freud-text-base text-coral-600 font-semibold mt-3">
                  Normally $299 &mdash; included complimentary with your new coach account.
                </p>
              </div>

              <div className="space-y-3">
                <a
                  href={bookingUrl || "https://calendly.com/patrickdahdal/profile-success-session"}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    logFunnelEvent('founder_session_booking_clicked');
                    supabase
                      .from('coaches')
                      .update({
                        free_profile_founder_session_booked: true,
                        free_profile_founder_session_booked_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                      })
                      .eq('id', coachId);
                  }}
                  className="freud-btn-primary w-full block text-center"
                >
                  Book My Complimentary Success Session
                </a>
                {(invitationSent || selfProfileStarted) ? (
                  <button onClick={completeOnboarding} className="w-full px-6 py-3 rounded-xl border-2 border-chocolate-200 text-chocolate-600 font-medium hover:bg-cream-100 transition-all">
                    Go to My Dashboard
                  </button>
                ) : (
                  <button onClick={completeOnboarding} className="w-full px-6 py-3 rounded-xl border-2 border-chocolate-200 text-chocolate-600 font-medium hover:bg-cream-100 transition-all">
                    Set Up My First Profile
                  </button>
                )}
              </div>

              <p className="text-center freud-text-xs text-chocolate-400 mt-4">
                The secondary option remains available so this step does not block your activation.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Back button */}
        {step > 1 && step < 4 && (
          <button
            onClick={() => setStep((step - 1) as WizardStep)}
            className="mt-6 flex items-center gap-2 text-chocolate-500 hover:text-chocolate-700 mx-auto transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        )}
      </div>
    </div>
  );
}
